/**
 * Spread onto a non-interactive element that acts as a link or button: adds
 * the role, keyboard focus, and key activation (Enter for links, Enter/Space
 * for buttons) that the native element gets for free. Prefer real <a>/<button>
 * where styles allow; these are for elements whose stylesheets target their
 * current tag.
 */
export const linkify = (onActivate: (e: any) => void, label?: string) => ({
  role: 'link' as const,
  tabIndex: 0,
  ...(label ? { 'aria-label': label } : {}),
  onClick: onActivate,
  onKeyDown: (e: KeyboardEvent) => {
    // Only activate for keys pressed on this element itself. A keydown whose
    // target is a descendant means focus is inside (a textarea, a nested
    // control) — preventDefault there would swallow the keystroke (SNGL-7195:
    // spaces never reached reply textareas nested in buttonized comments).
    if (e.target !== e.currentTarget) return;
    if (e.key === 'Enter') {
      e.preventDefault();
      onActivate(e);
    }
  },
});

export const buttonize = (onActivate: (e: any) => void, label?: string) => ({
  role: 'button' as const,
  tabIndex: 0,
  ...(label ? { 'aria-label': label } : {}),
  onClick: onActivate,
  onKeyDown: (e: KeyboardEvent) => {
    // See linkify: never hijack keys bubbling from a focused descendant.
    if (e.target !== e.currentTarget) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onActivate(e);
    }
  },
});
